import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Banknote,
  PiggyBank,
  AlertCircle,
  Loader2,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import {
  organiserDashboardApi,
  centsToAUD,
  formatAuDate,
} from "../../lib/events";
import { extractError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

function StatCard({ icon: Icon, label, value, sub, testid, tone = "default" }) {
  const toneCls =
    tone === "accent" ? "text-[var(--gt-accent)]" : "text-[var(--gt-ink)]";
  return (
    <div className="gt-card" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <Icon size={18} className="text-[var(--gt-ink-2)]" />
      </div>
      <div className="gt-label mb-1">{label}</div>
      <div
        className={`font-heading text-2xl sm:text-3xl font-black tracking-tighter leading-none ${toneCls}`}
        data-testid={`${testid}-value`}
      >
        {value}
      </div>
      {sub && (
        <div className="mt-1 text-xs text-[var(--gt-ink-3)]" data-testid={`${testid}-sub`}>
          {sub}
        </div>
      )}
    </div>
  );
}

export default function PayoutsDashboard() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const d = await organiserDashboardApi.payoutsSummary();
      setData(d);
      setError("");
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const agg = data?.aggregate;
  const isEmpty = !!data && (!agg || agg.orders_count === 0);

  return (
    <section className="p-6 sm:p-10" data-testid="payouts-dashboard-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="gt-label mb-2">Payouts</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
              Estimated payouts
            </h2>
            <p className="text-[var(--gt-ink-2)] text-sm mt-2 max-w-2xl">
              These are <strong>estimated</strong> payouts based on completed ticket sales
              in AUD. Actual settlement dates and confirmed payout status will appear here
              once your Australian bank account has been added and reconciled.
            </p>
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            className="gt-btn-ghost text-sm"
            data-testid="payouts-refresh-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loading && !data && (
          <div
            className="flex items-center gap-3 text-[var(--gt-ink-2)]"
            data-testid="payouts-loading"
          >
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Loading payouts…</span>
          </div>
        )}

        {error && (
          <div
            className="gt-card border-[var(--gt-accent)] text-[var(--gt-accent)]"
            data-testid="payouts-error"
          >
            {error}
          </div>
        )}

        {data && !error && (
          <>
            {/* Bank details prompt when missing */}
            {!data.bank_details_present && (
              <div
                className="gt-card-hard mb-6 border-[var(--gt-accent)]"
                data-testid="payouts-bank-prompt"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle
                    className="text-[var(--gt-accent)] shrink-0 mt-0.5"
                    size={20}
                  />
                  <div className="flex-1">
                    <div className="font-heading text-lg font-bold mb-1">
                      Add your Australian bank account
                    </div>
                    <p className="text-sm text-[var(--gt-ink-2)] mb-4">
                      Payouts settle in AUD to your Australian bank account. Add your BSB
                      and account details in your profile — no money will be paid out
                      until this is set up.
                    </p>
                    <Link
                      to="/dashboard/profile"
                      className="gt-btn-primary inline-flex text-sm"
                      data-testid="payouts-add-bank-cta"
                    >
                      Add bank details <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {isEmpty ? (
              <div className="gt-card-hard text-center p-10" data-testid="payouts-empty">
                <Banknote
                  size={32}
                  className="mx-auto text-[var(--gt-ink-3)] mb-3"
                  strokeWidth={1.5}
                />
                <div className="font-heading text-xl font-bold mb-2">
                  No payout data yet.
                </div>
                <p className="text-sm text-[var(--gt-ink-2)] max-w-md mx-auto">
                  Once buyers purchase tickets for your events, estimated payouts will
                  appear here in AUD.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    icon={PiggyBank}
                    label="Estimated payout (AUD)"
                    value={centsToAUD(agg.estimated_payout_cents || 0)}
                    sub="All events · after refunds"
                    testid="payout-stat-estimated"
                  />
                  <StatCard
                    icon={Banknote}
                    label="Gross sales (AUD)"
                    value={centsToAUD(agg.gross_sales_cents || 0)}
                    sub={`Tickets ${centsToAUD(agg.gross_ticket_cents || 0)} · Extras ${centsToAUD(
                      agg.addons_total_cents || 0,
                    )}`}
                    testid="payout-stat-gross"
                  />
                  <StatCard
                    icon={Banknote}
                    label="Refunds deducted (AUD)"
                    value={centsToAUD(agg.refunded_total_cents || 0)}
                    sub={
                      agg.refunded_total_cents
                        ? "Reduces estimated payout"
                        : "No refunds"
                    }
                    testid="payout-stat-refunds"
                    tone={agg.refunded_total_cents ? "accent" : "default"}
                  />
                  {isAdmin && (
                    <StatCard
                      icon={Banknote}
                      label="Booking fees (AUD)"
                      value={centsToAUD(agg.fee_cents || 0)}
                      sub="Already applied per order"
                      testid="payout-stat-fees"
                    />
                  )}
                </div>

                <div className="gt-card mt-6" data-testid="payouts-status-note">
                  <div className="flex items-start gap-3">
                    <AlertCircle
                      size={16}
                      className="text-[var(--gt-ink-2)] mt-0.5 shrink-0"
                    />
                    <div className="text-xs text-[var(--gt-ink-2)]">
                      <strong>Estimated</strong> means these figures are calculated from
                      completed orders and refunds. No money has been paid out yet —
                      settlement pipeline and payout status will be shown here in a
                      future update.
                    </div>
                  </div>
                </div>

                {/* Per-event breakdown */}
                <div className="gt-card mt-8" data-testid="payouts-per-event">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-xl font-black tracking-tight">
                      Estimated payouts by event
                    </h3>
                    <span className="gt-label">
                      {data.per_event.filter((e) => e.totals.orders_count > 0).length}{" "}
                      selling · {data.events_count} total
                    </span>
                  </div>
                  {data.per_event.filter((e) => e.totals.orders_count > 0).length === 0 ? (
                    <div
                      className="text-sm text-[var(--gt-ink-2)]"
                      data-testid="payouts-per-event-empty"
                    >
                      No event has payout data yet.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left border-b border-[var(--gt-line)]">
                            <th className="py-2 pr-3 gt-label">Event</th>
                            <th className="py-2 px-3 gt-label text-right">Gross</th>
                            <th className="py-2 px-3 gt-label text-right">Refunds</th>
                            {isAdmin && <th className="py-2 px-3 gt-label text-right">Fees</th>}
                            <th className="py-2 pl-3 gt-label text-right">Est. payout</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.per_event
                            .filter((e) => e.totals.orders_count > 0)
                            .map((e) => (
                              <tr
                                key={e.id}
                                className="border-b border-[var(--gt-line)]/60 hover:bg-[var(--gt-bg-surface)]"
                                data-testid={`payouts-event-row-${e.id}`}
                              >
                                <td className="py-3 pr-3">
                                  <div className="font-semibold text-[var(--gt-ink)] truncate max-w-[240px]">
                                    {e.title}
                                  </div>
                                  <div className="text-xs text-[var(--gt-ink-3)]">
                                    {formatAuDate(e.start_at)}
                                  </div>
                                </td>
                                <td className="py-3 px-3 text-right font-mono">
                                  {centsToAUD(e.totals.gross_sales_cents)}
                                </td>
                                <td className="py-3 px-3 text-right font-mono text-[var(--gt-accent)]">
                                  {e.totals.refunded_total_cents
                                    ? `−${centsToAUD(e.totals.refunded_total_cents)}`
                                    : "—"}
                                </td>
                                {isAdmin && (
                                  <td className="py-3 px-3 text-right font-mono">
                                    {centsToAUD(e.totals.fee_cents)}
                                  </td>
                                )}
                                <td className="py-3 pl-3 text-right font-mono font-bold">
                                  {centsToAUD(e.totals.estimated_payout_cents)}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
